import { useState } from 'react'
import { Link, useForm } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TaskForm, type TaskFormState } from './_components/TaskForm'
import type {
  AssigneeOption,
  TaskDetail,
  TaskSubject,
} from '@/types/work'

interface Props {
  task: TaskDetail
  assignee_options: AssigneeOption[]
  // Sheet deep-link prefill (already resolved + visibility-checked
  // server-side in tasks_controller#prefill_from_query, PR #84).
  prefill: TaskSubject | null
}

export default function TasksNew({ task, assignee_options, prefill }: Props) {
  const [state, setState] = useState<TaskFormState>({
    title: task.title ?? '',
    description: task.description ?? '',
    priority: task.priority,
    task_type: task.task_type,
    due_at: task.due_at ?? '',
    assignee_user_id: task.assignee_user_id ?? null,
    subject: prefill ?? task.subject ?? null,
  })

  const { post, processing, errors } = useForm({})

  function update<K extends keyof TaskFormState>(key: K, value: TaskFormState[K]) {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  function submit(e: React.FormEvent) {
    e.preventDefault()
    post('/work/tasks', {
      preserveScroll: true,
      // Inertia's useForm posts `data`; we passed `{}` so we override here
      // with the controller's expected `task` namespace. (Wrapping by
      // hand keeps the field control simpler than threading useForm's
      // setData through TaskForm.)
      data: {
        task: {
          title: state.title,
          description: state.description,
          priority: state.priority,
          task_type: state.task_type,
          due_at: state.due_at || null,
          assignee_user_id: state.assignee_user_id,
          subject_type: state.subject?.type ?? null,
          subject_id: state.subject?.id ?? null,
        },
      },
    } as Parameters<typeof post>[1])
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Link
          href="/work/tasks"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Tâches
        </Link>
        <h1 className="text-2xl font-bold tracking-tight">Nouvelle tâche</h1>
      </div>

      <TaskForm
        state={state}
        onChange={update}
        onSubmit={submit}
        errors={errors as Record<string, string>}
        processing={processing}
        assigneeOptions={assignee_options}
        actions={
          <>
            <Button type="button" variant="ghost" asChild>
              <Link href="/work/tasks">Annuler</Link>
            </Button>
            <Button type="submit" disabled={processing}>
              Créer
            </Button>
          </>
        }
      />
    </div>
  )
}
